import { config } from '@cfg';
import GoogleMapResults from '@components/GoogleMapResults';
import { AspectBox } from '@components/Layout';
import Block from '@components/Layout/Block';
import { color, font, query, space } from '@design/templateFns';
import { useTranslations } from 'next-intl';
import { ReactNode, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

interface LocationProps {
  state: string;
  city: string;
  zip: string;
  neighborhood: string;
  address: string;
  center: any;
  mappedResults?: any;
  showDrawAreaButton?: boolean;
}

const Location = ({
  state,
  city,
  zip,
  neighborhood,
  address,
  center,
  mappedResults,
  showDrawAreaButton = true,
}: LocationProps): ReactNode => {
  const t = useTranslations();
  const { ref, inView } = useInView();
  const [showMap, setShowMap] = useState(false);

  // Show the map after it is in view. Use state so it doesn't hide when it is scrolled away from.
  useEffect(() => {
    if (inView) {
      setShowMap(true);
    }
  }, [inView]);

  return (
    <Block narrow fullOnMobile>
      <Header data-testid="location-header">{t('Location')}</Header>
      <Breadcrumbs data-testid="location-breadcrumbs">
        {state && <Crumb data-testid="state-breadcrumb">{state}</Crumb>}
        {city && <Crumb data-testid="city-breadcrumb">{city} </Crumb>}
        {zip && <Crumb data-testid="zip-breadcrumb">{zip}</Crumb>}
        {neighborhood && <Crumb data-testid="neighborhood-breadcrumb">{neighborhood}</Crumb>}
        {address && <Crumb data-testid="address-breadcrumb">{address}</Crumb>}
      </Breadcrumbs>

      <div ref={ref}>
        {showMap && center.lat && center.lng && mappedResults && (
          <GoogleMapResultsWrapper data-testid="google-map-wrapper">
            <AspectBox $ratio={[5 / 8, 44 / 25]}>
              <GoogleMapResults
                center={center}
                mappedResults={mappedResults}
                showDrawAreaButton={showDrawAreaButton}
                config={config}
                zoomValue={15}
                isMarkerHover
              />
            </AspectBox>
          </GoogleMapResultsWrapper>
        )}
      </div>
    </Block>
  );
};

export default Location;

/*** STYLING ***/
const Header = styled.h2`
  ${font('headline-xxs')}
  color: ${color('fg')};
  padding-bottom: ${space('xl')};

  @media screen and (${query.atLeast('tablet')}) {
    ${font('headline-sm')}
  }
  @media (${query.below('tablet')}) {
    margin-left: ${space('xl')};
  }
`;

const Breadcrumbs = styled.ul`
  list-style: none;
  padding: 0 0 ${space('x10')} 0;
  line-height: 2;

  @media screen and (${query.below('tablet')}) {
    margin-left: ${space('xl')};
    padding: 0 0 ${space('x6')} 0;
  }
`;

const Crumb = styled.li`
  display: inline-flex;
  align-items: center;
  ${font('body-md-light')};
  color: ${color('gray4')};

  &:after {
    content: url("data:image/svg+xml,%3Csvg version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' viewBox='0 0 8 16' xml:space='preserve'%3E%3Cpath fill='none' stroke='%23757575' stroke-width='1.5' d='M2,12.5l4.5-4.68L2,3.5'/%3E%3C/svg%3E");
    color: ${color('gray4')}; // svg color is set to currentColor
    display: inline-block;
    width: 0.5em;
    height: 1em;
    margin: 0 ${space('sm')} 2px ${space('sm')};
  }

  &:last-child {
    color: ${color('fg')};
    ${font('body-md')};

    &:after {
      content: '';
      padding: 0;
    }
  }

  @media screen and (${query.below('tablet')}) {
    ${font('body-sm-light')};

    &:last-child {
      ${font('body-sm')};
    }
  }
`;

const GoogleMapResultsWrapper = styled.div`
  .map-container {
    width: 100%;
    height: 100%;
    min-height: auto;
  }
`;
